import request from "../../../util/SuperagentUtils";
import {formatDateMMDDyyyy} from "../../../util/DateUtils";
import {supplierAccountsReceivableReportEndpoints} from "../../api-endpoints";
import {SupplierAccountsReceivableRequestType} from "../../../types/reports/SupplierAccountsReceivableRequestType";
import {getUrlSearchParamsByPaging} from "../../../util/UrlPagingParamUtils";

const getParameters = (searchParams: SupplierAccountsReceivableRequestType, paging: boolean, isPdfReport: boolean) => {
  const queryParams = paging && !isPdfReport ? getUrlSearchParamsByPaging(searchParams.page, searchParams.size) : new URLSearchParams();

  if (searchParams.buyerAccountId) queryParams.append("buyerAccountId", searchParams.buyerAccountId);
  if (searchParams.locationId) queryParams.append("locationId", searchParams.locationId);

  if (!isPdfReport) {
    if (searchParams.startDate) queryParams.append("startDate", formatDateMMDDyyyy(searchParams.startDate));
    if (searchParams.endDate) queryParams.append("endDate", formatDateMMDDyyyy(searchParams.endDate));
    if (searchParams.bookingNumber) queryParams.append("bookingNumber", searchParams.bookingNumber);
    if (searchParams.transactionNumber) queryParams.append("transactionNumber", searchParams.transactionNumber);
    queryParams.append("isUnpaidOnly", String(searchParams.isUnpaidOnly));
    if (searchParams.sortBy) queryParams.append("sortBy", searchParams.sortBy);
    if (searchParams.sortDir) queryParams.append("sortDir", searchParams.sortDir);
  }

  return queryParams;
}

/**
 * Fetches supplier accounts receivable data based on the provided search parameters. If onSuccess and onFail callbacks are provided, it will handle the response accordingly; otherwise, it returns a promise.
 * @param searchParams
 * @param onSuccess
 * @param onFail
 */
export const getSupplierAccountsReceivable = (
  searchParams: SupplierAccountsReceivableRequestType,
  onSuccess?: () => void,
  onFail?: () => void
) => {
  const result = request.get(`${supplierAccountsReceivableReportEndpoints.search}?${getParameters(searchParams, true, false)}`);

  if (onSuccess && onFail) {
    result
      .then(onSuccess)
      .catch(onFail)
  } else {
    return result
  }
}

export const getSupplierAccountsReceivableCSVReport = (searchParams: SupplierAccountsReceivableRequestType) => {
  return `${supplierAccountsReceivableReportEndpoints.report}?${getParameters(searchParams, false, false).toString()}`;
}

export const getSupplierAccountsReceivableBuyerLocationPdf = (searchParams: SupplierAccountsReceivableRequestType) => {
  return `${supplierAccountsReceivableReportEndpoints.buyerLocationPdf}?${getParameters(searchParams, false, true).toString()}`;
}

/**
 * Fetches the aggregated accounts-receivable summary (total outstanding plus
 * the aged buckets) for a date range, optionally scoped to one location.
 *
 * Same endpoint the Accounts Receivable report's summary bar uses, so the two
 * agree. Amounts come back as integer cents.
 */
export const getSupplierAccountsReceivableSummary = (
  searchParams: Partial<SupplierAccountsReceivableRequestType>,
) => {
  const params = {
    startDate: '',
    endDate: '',
    locationId: '',
    bookingNumber: '',
    transactionNumber: '',
    buyerId: '',
    isUnpaidOnly: true,
    sortBy: '',
    sortDir: '',
    ...searchParams,
  } satisfies SupplierAccountsReceivableRequestType;

  return request.get(
    `${supplierAccountsReceivableReportEndpoints.summary}?${getParameters(params, false, false)}`,
  );
};
